/**
 * Sprite generati a runtime. Il mondo e i mostri usano gli asset CraftPix
 * (docs/CREDITS.md); qui resta il protagonista — un "chibi" cartoon hi-res
 * disegnato su canvas, pensato per essere sostituito da un character pack
 * CraftPix senza toccare il renderer.
 */

const OUTLINE = '#33272b';

function makeCanvas(w: number, h: number): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D non disponibile');
  return [canvas, ctx];
}

export type Direction = 'su' | 'giu' | 'sinistra' | 'destra';

const SKIN = '#f8d4ae';
const SKIN_SHADE = '#e8b88c';
const HAIR = '#5d4030';
const JACKET = '#3f9e6e';
const JACKET_SHADE = '#2f7b54';
const PANTS = '#4a5878';
const HAT = '#e8c06a';
const HAT_SHADE = '#caa050';
const BAG = '#a8703f';

/**
 * Il Custode di Faglia in stile cartoon (canvas 72×96, da scalare a ~0.5).
 * `step` 0..3: ciclo di camminata (0 e 2 = appoggio, 1 e 3 = falcata).
 */
export function drawPlayerHD(dir: Direction, step: number): HTMLCanvasElement {
  const W = 72;
  const H = 96;
  const [canvas, ctx] = makeCanvas(W, H);
  const cx = W / 2;
  ctx.lineWidth = 4;
  ctx.strokeStyle = OUTLINE;
  ctx.lineJoin = 'round';

  const phase = step % 4;
  const swing = phase === 1 ? 1 : phase === 3 ? -1 : 0; // falcata avanti/indietro
  const bob = phase % 2 === 1 ? 2 : 0;

  const side = dir === 'sinistra' || dir === 'destra';
  const flip = dir === 'sinistra';
  if (flip) {
    ctx.translate(W, 0);
    ctx.scale(-1, 1);
  }

  const roundRect = (x: number, y: number, w: number, h: number, r: number, fill: string) => {
    ctx.fillStyle = fill;
    ctx.beginPath();
    ctx.roundRect(x, y, w, h, r);
    ctx.fill();
    ctx.stroke();
  };

  // ——— ombra a terra ———
  ctx.save();
  ctx.fillStyle = 'rgba(40,30,30,0.25)';
  ctx.beginPath();
  ctx.ellipse(cx, H - 5, 17, 5, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  // ——— gambe ———
  const legY = H - 26 + bob;
  if (side) {
    roundRect(cx - 9 + swing * 5, legY, 9, 20 - bob, 4, PANTS);
    roundRect(cx + 0 - swing * 5, legY, 9, 20 - bob, 4, PANTS);
  } else {
    roundRect(cx - 11, legY + (swing > 0 ? 2 : 0), 9, 20 - bob - (swing > 0 ? 2 : 0), 4, PANTS);
    roundRect(cx + 2, legY + (swing < 0 ? 2 : 0), 9, 20 - bob - (swing < 0 ? 2 : 0), 4, PANTS);
  }

  // ——— corpo (giacca da ricercatore) ———
  const bodyY = H - 50 + bob;
  roundRect(cx - 14, bodyY, 28, 28, 9, JACKET);
  // ombreggiatura cel
  ctx.save();
  ctx.fillStyle = JACKET_SHADE;
  ctx.beginPath();
  ctx.roundRect(cx - 14, bodyY + 16, 28, 12, [0, 0, 9, 9]);
  ctx.fill();
  ctx.restore();

  // tracolla / zaino
  if (dir === 'su') {
    roundRect(cx - 10, bodyY + 2, 20, 18, 6, BAG);
  } else if (!side) {
    ctx.save();
    ctx.strokeStyle = BAG;
    ctx.lineWidth = 5;
    ctx.beginPath();
    ctx.moveTo(cx - 11, bodyY + 3);
    ctx.lineTo(cx + 11, bodyY + 14);
    ctx.stroke();
    ctx.restore();
  } else {
    roundRect(cx - 16, bodyY + 8, 10, 13, 4, BAG);
  }

  // ——— braccia ———
  const armY = bodyY + 4;
  ctx.fillStyle = JACKET;
  if (side) {
    roundRect(cx - 5 - swing * 6, armY + 4, 9, 16, 4, JACKET);
  } else {
    roundRect(cx - 19, armY + (swing < 0 ? -2 : 2), 8, 16, 4, JACKET);
    roundRect(cx + 11, armY + (swing > 0 ? -2 : 2), 8, 16, 4, JACKET);
  }

  // ——— testa ———
  const headY = H - 62 + bob;
  ctx.fillStyle = SKIN;
  ctx.beginPath();
  ctx.arc(cx, headY, 17, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  // guancia in ombra
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, headY, 17, 0, Math.PI * 2);
  ctx.clip();
  ctx.fillStyle = SKIN_SHADE;
  ctx.beginPath();
  ctx.arc(cx, headY + 10, 17, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  // capelli / retro testa
  if (dir === 'su') {
    ctx.fillStyle = HAIR;
    ctx.beginPath();
    ctx.arc(cx, headY, 16.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
  } else {
    ctx.fillStyle = HAIR;
    ctx.beginPath();
    ctx.arc(cx, headY - 3, 16.5, Math.PI * (side ? 0.95 : 1.05), Math.PI * (side ? 2.05 : 1.95));
    ctx.fill();
  }

  // ——— cappello da esploratore ———
  ctx.fillStyle = HAT;
  ctx.beginPath();
  ctx.ellipse(cx, headY - 8, 21, 8, 0, Math.PI, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  ctx.beginPath();
  ctx.ellipse(cx, headY - 8, 21, 6.5, 0, 0, Math.PI);
  ctx.fillStyle = HAT_SHADE;
  ctx.fill();
  ctx.stroke();
  roundRect(cx - 12, headY - 24, 24, 14, 7, HAT);

  // ——— viso ———
  if (dir !== 'su') {
    ctx.fillStyle = OUTLINE;
    if (side) {
      ctx.beginPath();
      ctx.ellipse(cx + 8, headY + 1, 2.6, 3.4, 0, 0, Math.PI * 2);
      ctx.fill();
      // sorriso
      ctx.save();
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.arc(cx + 11, headY + 7, 3.5, 0.2, Math.PI * 0.75);
      ctx.stroke();
      ctx.restore();
    } else {
      ctx.beginPath();
      ctx.ellipse(cx - 6, headY + 1, 2.6, 3.4, 0, 0, Math.PI * 2);
      ctx.ellipse(cx + 6, headY + 1, 2.6, 3.4, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.save();
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.arc(cx, headY + 6, 4.5, 0.25, Math.PI - 0.25);
      ctx.stroke();
      ctx.restore();
      // guance
      ctx.fillStyle = 'rgba(240,140,120,0.45)';
      ctx.beginPath();
      ctx.ellipse(cx - 11, headY + 6, 3, 2, 0, 0, Math.PI * 2);
      ctx.ellipse(cx + 11, headY + 6, 3, 2, 0, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  return canvas;
}
